import React from 'react'
import { CognitoUser, CognitoUserAttribute } from 'amazon-cognito-identity-js'
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom'
import { RouteChildrenProps } from 'react-router'
import Amplify from 'aws-amplify'
import * as Sentry from '@sentry/browser'

import AMPLIFY_CONFIG from './config/amplify-config'
import SENTRY_CONFIG from './config/sentry-config'

import loadingBox from './assets/images/loading_box.gif'
import './App.css'

import { ArticlesContextProvider, AuthContext, AuthData, RefreshAuthData, UserAttributes } from './contexts'

import { NotificationProvider } from './contexts/NotificationProvider'
import { NotificationContextInterface } from './contexts/NotificationContext'

import { Notification } from './components'

import { Layout } from './pages/Layout/Layout'
import { LoginPage } from './pages/LoginPage/LoginPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage/ForgotPasswordPage'
import { VerifyPasswordPage } from './pages/VerifyPasswordPage/VerifyPasswordPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage/VerifyEmailPage'
import './utils/finally.polyfill.js'
import { signOut } from './utils/api'

Amplify.configure(AMPLIFY_CONFIG)

Sentry.init(SENTRY_CONFIG)

interface State {
  userData: CognitoUser | null
  userAttributes: UserAttributes | null
}

class App extends React.Component<{}, State> {
  state: State = {
    userData: null,
    userAttributes: null,
  }

  authData: AuthData = new AuthData()
  private timer: any

  componentDidMount(): void {
    this.timer = setInterval(() => {
      const date = Number(sessionStorage.getItem('expiration_date'))
      if (new Date().getTime() > date) {
        signOut()
        return
      }
    }, 10 * 60 * 1000)
    this.refreshAuthData()
  }

  componentDidCatch(error: Error): void {
    Sentry.captureException(error)
  }

  componentWillUnmount() {
    clearInterval(this.timer)
  }

  render() {
    return (
      <NotificationProvider>
        <div className="container body">
          {this.state.userData ? (
            <AuthContext.Provider
              value={{
                userAttributes: this.state.userAttributes!,
                userData: this.state.userData!,
                refreshAuthData: this.refreshAuthData,
                hasGroup: this.authData.hasGroup,
                getUserGroups: this.authData.getUserGroups,
                getGroupDefaultRoute: this.authData.getGroupDefaultRoute,
              }}
            >
              <Notification />
              <Router>
                {Object.keys(this.state.userData!).length > 0 ? (
                  <ArticlesContextProvider>
                    <Route path="/" component={Layout} />
                  </ArticlesContextProvider>
                ) : (
                  <Switch>
                    <Route
                      path="/login"
                      exact
                      // TODO figure out how to compose HOC functions
                      component={(props: RouteChildrenProps & NotificationContextInterface) => (
                        <LoginPage refreshAuthData={this.refreshAuthData} {...props} />
                      )}
                    />
                    <Route path="/forgot-password" exact component={ForgotPasswordPage} />
                    <Route path="/verify-password" exact component={VerifyPasswordPage} />
                    <Route path="/verify-email" exact component={VerifyEmailPage} />
                    <Redirect to="/login" />
                  </Switch>
                )}
              </Router>
            </AuthContext.Provider>
          ) : (
            <img src={loadingBox} className="loading" alt="loading" />
          )}
        </div>
      </NotificationProvider>
    )
  }

  private getUserAttributes = (userData: CognitoUser): Promise<UserAttributes> => {
    return new Promise(resolve => {
      userData.getUserAttributes((error, attributes = []) => {
        const userAttributes: UserAttributes = {}
        if (!attributes) return
        attributes.forEach((attribute: CognitoUserAttribute) => {
          switch (attribute.getName()) {
            case 'custom:companyName':
              userAttributes.companyName = attribute.getValue()
              break
            case 'custom:location':
              userAttributes.locations = JSON.parse(attribute.getValue())
              break
            default:
              break
          }
        })
        userAttributes.roles = this.authData.getUserGroups()
        resolve(userAttributes)
      })
    })
  }

  private refreshAuthData: RefreshAuthData = () => {
    this.authData
      .getRefreshedAuthData()
      .then((userData: CognitoUser) => {
        this.authData.setAuthData(userData)
        return this.getUserAttributes(userData).then((userAttributes: UserAttributes) => ({
          userData,
          userAttributes,
        }))
      })
      .then(({ userData, userAttributes }) => {
        this.setState({ userData, userAttributes })
      })
      .catch(() => this.setState({ userData: {} as CognitoUser }))
  }
}

export default App
